// Client-side envelope crypto for passkey-PRF encryption at rest (HKDF-SHA256, AES-256-GCM); the
// vectors in envelope_test.mjs are the iOS contract.
// PRF output derives only a KEK that wraps a random stable DEK, because PRF output can drift and a
// key derived from it would lose the data.

const webcrypto = globalThis.crypto;
const subtle = webcrypto.subtle;
const enc = new TextEncoder();

export const ENVELOPE_VERSION = 1;              // byte[0] of every sealed blob
const PRF_SALT_PREFIX = "coglative/prf/v1/";    // + tenantId  → prf.eval.first
const HKDF_SALT = new Uint8Array(32);           // 32 fixed 0x00 bytes (== RFC5869 default salt)
const HKDF_INFO = "coglative/kek/v1";           // KEK derivation info (NO tenantId; see prfSalt)
const DEK_WRAP_AAD_PREFIX = "coglative/dek-wrap/v1|"; // + tenantId + "|" + credentialId
const FIELD_AAD_PREFIX = "coglative/field/v1|"; // + recordUri + "|" + fieldName

export function b64url(bytes) {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export function b64urlDecode(str) {
  const b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
export function hex(bytes) {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}
export function fromHex(str) {
  if (str.length % 2 !== 0) throw new Error("fromHex: odd-length string");
  const out = new Uint8Array(str.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(str.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function toBytes(x) {
  if (x instanceof Uint8Array) return x;
  if (x instanceof ArrayBuffer) return new Uint8Array(x);
  if (ArrayBuffer.isView(x)) return new Uint8Array(x.buffer, x.byteOffset, x.byteLength);
  if (typeof x === "string") return enc.encode(x);
  throw new TypeError("expected string | Uint8Array | ArrayBuffer");
}

// The prf.eval.first salt; tenant separation happens here, so tenantId does not enter the HKDF.
export function prfSalt(tenantId) {
  if (!tenantId) throw new Error("prfSalt: tenantId required");
  return enc.encode(PRF_SALT_PREFIX + tenantId);
}

// AAD binding a DEK-wrap to its tenant + credential, so a blob cannot be replayed under another.
export function dekWrapAad(tenantId, credentialId) {
  if (!tenantId || !credentialId) throw new Error("dekWrapAad: tenantId + credentialId required");
  return enc.encode(DEK_WRAP_AAD_PREFIX + tenantId + "|" + credentialId);
}

export function fieldAad(recordUri, fieldName) {
  if (!recordUri || !fieldName) throw new Error("fieldAad: recordUri + fieldName required");
  return enc.encode(FIELD_AAD_PREFIX + recordUri + "|" + fieldName);
}

// KEK = HKDF-SHA256(ikm=prfOutput, salt=32x00, info="coglative/kek/v1", L=32); tenantId is checked
// but does not enter the HKDF.
export async function deriveKekBits(prfOutput, tenantId) {
  const ikm = toBytes(prfOutput);
  if (ikm.length !== 32) throw new Error("deriveKekBits: prfOutput must be 32 bytes");
  if (!tenantId) throw new Error("deriveKekBits: tenantId required");
  const base = await subtle.importKey("raw", ikm, "HKDF", false, ["deriveBits"]);
  const bits = await subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: HKDF_SALT, info: enc.encode(HKDF_INFO) },
    base, 256);
  return new Uint8Array(bits);
}

export async function deriveKek(prfOutput, tenantId) {
  const bits = await deriveKekBits(prfOutput, tenantId);
  return subtle.importKey("raw", bits, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

// A random 32-byte DEK, minted once per tenant and wrapped under each credential's KEK.
export function newDek() {
  const dek = new Uint8Array(32);
  webcrypto.getRandomValues(dek);
  return dek;
}

// Envelope: [0] version, [1..13) 12-byte nonce, [13..] ct || tag, base64url without padding.
// `ivOverride` is for tests only.
async function seal(key, plaintext, aad, ivOverride) {
  const iv = ivOverride ? toBytes(ivOverride) : webcrypto.getRandomValues(new Uint8Array(12));
  if (iv.length !== 12) throw new Error("seal: iv must be 12 bytes");
  const ctTag = new Uint8Array(await subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: toBytes(aad), tagLength: 128 },
    key, toBytes(plaintext)));
  const out = new Uint8Array(1 + 12 + ctTag.length);
  out[0] = ENVELOPE_VERSION;
  out.set(iv, 1);
  out.set(ctTag, 13);
  return b64url(out);
}

async function open(key, blob, aad) {
  const raw = (typeof blob === "string") ? b64urlDecode(blob) : toBytes(blob);
  if (raw.length < 1 + 12 + 16) throw new Error("open: blob too short");
  if (raw[0] !== ENVELOPE_VERSION) throw new Error("open: unsupported envelope version " + raw[0]);
  const iv = raw.subarray(1, 13);
  const ctTag = raw.subarray(13);
  // AES-GCM decrypt rejects on tag or AAD mismatch, which is the tamper check.
  const pt = await subtle.decrypt(
    { name: "AES-GCM", iv, additionalData: toBytes(aad), tagLength: 128 }, key, ctTag);
  return new Uint8Array(pt);
}

export async function wrapDek(kek, dek, tenantId, credentialId, ivOverride) {
  const dekBytes = toBytes(dek);
  if (dekBytes.length !== 32) throw new Error("wrapDek: dek must be 32 bytes");
  return seal(kek, dekBytes, dekWrapAad(tenantId, credentialId), ivOverride);
}

// Throws if the blob was tampered with or wrapped under a different tenant/credential (AAD).
export async function unwrapDek(kek, wrapped, tenantId, credentialId) {
  const dek = await open(kek, wrapped, dekWrapAad(tenantId, credentialId));
  if (dek.length !== 32) throw new Error("unwrapDek: recovered DEK is not 32 bytes");
  return dek;
}

export function wrappedDekRecord(tenantId, credentialId, wrappedDek) {
  return { tenantId, credentialId, wrappedDek };
}

async function importDek(dek) {
  const bytes = toBytes(dek);
  if (bytes.length !== 32) throw new Error("field crypto: dek must be 32 bytes");
  return subtle.importKey("raw", bytes, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

// Encrypt one field under the DEK; pass fieldAad(recordUri, fieldName) as `aad`.
export async function encryptField(dek, bytes, aad, ivOverride) {
  const key = await importDek(dek);
  return seal(key, bytes, aad, ivOverride);
}

// Throws on tamper or AAD mismatch. Decode with a TextDecoder if the field is text.
export async function decryptField(dek, blob, aad) {
  const key = await importDek(dek);
  return open(key, blob, aad);
}
